import {Attributes, Card, DataStatus} from 'platform/components';

import {useMemo} from 'react';

import {useGetVehicleQuery} from '@dms/api/sales';
import {useGetServiceVehicleV2Query, useGetVehicleClassesQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';

import {composePath, useNavigate} from 'shared/web';

type ServiceConditionsCardProps = {
  vehicleId: string;
  canEditVehicle: boolean;
};

export function ServiceConditionsCard({vehicleId, canEditVehicle}: ServiceConditionsCardProps) {
  const navigate = useNavigate();
  const {data: vehicleDetail, isLoading, isError} = useGetVehicleQuery({vehicleId});

  const {data: serviceVehicle} = useGetServiceVehicleV2Query({vehicleId});

  const {data: vehicleClasses} = useGetVehicleClassesQuery();

  const vehicleClass = useMemo(
    () => vehicleClasses?.find((vc) => vc.vehicleClassId === serviceVehicle?.vehicleClassId),
    [vehicleClasses, serviceVehicle]
  );

  const handleOnClick = () => {
    navigate(composePath(vehiclesRoutes.editV2Tab, {params: {id: vehicleId, tab: 'vehicle-data'}}));
  };

  return (
    <Card
      title={i18n.t('entity.vehicle.labels.generalData')}
      actions={
        canEditVehicle
          ? [
              {
                type: 'button',
                variant: 'link',
                leftIcon: 'image/edit',
                onClick: handleOnClick,
                'data-testid': testIds.vehicles.vehicleData('serviceConditionsCard-edit'),
                title: i18n.t('general.actions.edit'),
              },
            ]
          : undefined
      }
      data-testid={testIds.vehicles.vehicleData('serviceConditionsCard')}
    >
      <DataStatus isLoading={isLoading} isError={isError}>
        <Attributes
          data-testid={testIds.vehicles.vehicleData('serviceConditionsCard-parameters')}
          rows={[
            {
              label: i18n.t('entity.vehicleClass.labels.vehicleClass'),
              value: vehicleClass?.name,
              testId: 'vehicleClass',
            },
            {
              label: i18n.t('entity.vehicle.labels.otherRecords'),
              value: vehicleDetail?.vehicle?.otherRecords,
              testId: 'otherRecords',
            },
          ]}
        />
      </DataStatus>
    </Card>
  );
}
